import { GameAction, useGameActionDispatch } from "app/game/useGameState";
import { GODOT_EVENT, useGodotListener } from "app/godot-embed/godot-bridge";
import { useCallback } from "react";
import { type TutorialLevel } from "./tutorial-data";
import useTutorialState, {
  TutorialAction,
  useTutorialActionDispatch,
} from "./useTutorialState";

export default function useTutorialLevel() {
  const tutorialDispatch = useTutorialActionDispatch();
  const gameDispatch = useGameActionDispatch();
  const {
    phaseIndex: tutorialPhaseIndex,
    levelData: tutorialData,
    isInTutorial,
    phaseData: tutorialPhase,
  } = useTutorialState();

  const start = useCallback(
    (levelData: TutorialLevel) => {
      gameDispatch({
        type: GameAction.TUTORIAL_LEVEL_START,
      });
      tutorialDispatch({
        type: TutorialAction.START_TUTORIAL_LEVEL,
        payload: { levelData },
      });
    },
    [gameDispatch, tutorialDispatch],
  );

  const advancePhase = useCallback(() => {
    // If all phases of the current tutorial have been completed, end it.
    if (tutorialPhaseIndex === (tutorialData?.phases.length ?? 1) - 1) {
      tutorialDispatch({ type: TutorialAction.FINISH_TUTORIAL });
      gameDispatch({
        type: GameAction.TUTORIAL_LEVEL_FINISH,
        payload: { levelName: tutorialData?.name ?? "UNKNOWN" },
      });
      return;
    }

    tutorialDispatch({ type: TutorialAction.ADVANCE_TUTORIAL });
  }, [tutorialDispatch, tutorialPhaseIndex, tutorialData, gameDispatch]);

  useGodotListener(GODOT_EVENT.TUTORIAL_PHASE_COMPLETED, advancePhase);

  // Advance tutorial when the puzzle is solved
  const handleTutorialSolved = useCallback(() => {
    if (isInTutorial && tutorialPhase?.waitFor === "puzzleComplete") {
      advancePhase();
    }
  }, [isInTutorial, tutorialPhase, advancePhase]);

  useGodotListener(GODOT_EVENT.TUTORIAL_SOLVED, handleTutorialSolved);

  return { start, advancePhase };
}
